import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { categories, projects, newsArticles, locations } from '../data/sbbData';
import { ArrowRight, ChevronRight, ChevronLeft, MapPin, Play, CheckCircle } from 'lucide-react';
import HomeCalculator from '../components/HomeCalculator';

export default function Home() {
  const { lang, t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [autoplay, setAutoplay] = useState(true);

  const heroSlides = [
    {
      id: 1,
      title: t('hero_title_1'),
      desc: t('hero_desc_1'),
      ctaText: t('cta_explore_products'),
      ctaLink: '/produk-layanan',
      image: '/reference-images/hero-slider.webp'
    },
    {
      id: 2,
      title: t('hero_title_2'),
      desc: t('hero_desc_2'),
      ctaText: t('cta_view_projects'),
      ctaLink: '/pelanggan-proyek',
      image: '/reference-images/project-cisumdawu.webp'
    }
  ];

  useEffect(() => {
    if (!autoplay) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [autoplay, heroSlides.length]);

  return (
    <div className="home-page">
      {/* 1. Hero Slider Section (Section 5.1) */}
      <section 
        className="hero-slider"
        onMouseEnter={() => setAutoplay(false)}
        onMouseLeave={() => setAutoplay(true)}
      >
        {heroSlides.map((slide, index) => (
          <div 
            key={slide.id} 
            className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
            style={{ backgroundImage: `linear-gradient(90deg, rgba(0,0,0,.12) 0%, rgba(0,0,0,.28) 42%, rgba(0,0,0,.8) 100%), url(${slide.image})` }}
          >
            <div className="container hero-container">
              <div className="hero-caption-box">
                <span className="hero-eyebrow">SOLUSI BANGUN BETON</span>
                <h1 className="hero-title">{slide.title}</h1>
                <p className="hero-desc">{slide.desc}</p>
                <div className="hero-cta-group">
                  <Link to={slide.ctaLink} className="btn btn-primary">
                    <span>{slide.ctaText}</span>
                    <ArrowRight size={18} />
                  </Link>
                  <Link to="/kontak-kami" className="btn btn-white">
                    <span>{t('cta_consultation')}</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}

        <button
          className="hero-arrow hero-arrow-prev"
          onClick={() => setCurrentSlide((currentSlide - 1 + heroSlides.length) % heroSlides.length)}
          aria-label="Slide sebelumnya"
        >
          <ChevronLeft size={28} />
        </button>
        <button
          className="hero-arrow hero-arrow-next"
          onClick={() => setCurrentSlide((currentSlide + 1) % heroSlides.length)}
          aria-label="Slide berikutnya"
        >
          <ChevronRight size={28} />
        </button>

        {/* Hero Slider Dots */}
        <div className="hero-dots">
          {heroSlides.map((_, index) => (
            <button 
              key={index}
              className={`dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => setCurrentSlide(index)}
              title={`Slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* 2. Sekilas Tentang Kami (Section 5.1) */}
      <section className="section about-reference-section">
        <div className="container">
          <div className="grid-2 align-center">
            <div className="about-preview-text">
              <span className="accent-bar"></span>
              <span className="eyebrow">Profil Perusahaan</span>
              <h2 className="section-title">{t('section_about_title')}</h2>
              <p className="about-desc-text">
                {t('section_about_desc')}
              </p>

              <Link to="/tentang-kami" className="btn btn-secondary">
                <span>Pelajari Profil Selengkapnya</span>
                <ChevronRight size={16} />
              </Link>
            </div>

            <div className="about-preview-image">
              <img 
                src="/reference-images/about-company.webp" 
                alt="Fasilitas Solusi Bangun Beton" 
                className="img-rounded-shadow"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 3. Solusi dan Layanan (Section 5.1 & 5.3) */}
      <section className="section section-subtle solutions-reference-section">
        <div className="container">
          <div className="section-header text-center">
            <span className="accent-bar"></span>
            <span className="eyebrow">Solusi Terintegrasi</span>
            <h2 className="section-title">{t('section_solutions_title')}</h2>
            <p className="section-subtitle">{t('section_solutions_subtitle')}</p>
          </div>

          <div className="services-carousel">
            {categories.map((cat) => (
              <div key={cat.slug} className="sbb-card">
                <img src={cat.image} alt={cat.name} className="sbb-card-img" />
                <div className="sbb-card-body">
                  <span className="sbb-card-tag">{cat.type === 'material' ? 'Agregat' : 'Solusi Beton'}</span>
                  <h3 className="sbb-card-title">{lang === 'en' ? cat.name_en : cat.name}</h3>
                  <p className="sbb-card-desc">{cat.description}</p>
                  <Link to={`/produk-layanan/${cat.slug}`} className="btn btn-outline-red btn-sm">
                    <span>Lihat Detail Solusi</span>
                    <ChevronRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <HomeCalculator />

      {/* 4. Pelanggan & Proyek Mahakarya (Section 5.1 & 5.8) */}
      <section className="section projects-reference-section">
        <div className="container">
          <div className="section-header">
            <span className="accent-bar"></span>
            <span className="eyebrow">Rekam Jejak & Kredibilitas</span>
            <h2 className="section-title">{t('section_featured_projects')}</h2>
            <p className="section-subtitle">{t('section_projects_subtitle')}</p>
          </div>

          <div className="projects-carousel">
            {projects.map((proj, index) => (
              <div key={proj.slug} className="sbb-card project-feature-card">
                <img src={proj.image} alt={proj.name} className="sbb-card-img" />
                <div className="sbb-card-body">
                  <span className="project-number">{String(index + 1).padStart(2, '0')}</span>
                  <span className="sbb-card-tag">{proj.location} • {proj.year}</span>
                  <h3 className="sbb-card-title">{proj.name}</h3>
                  <p className="sbb-card-desc">{proj.summary}</p>
                  <Link to={`/proyek/${proj.slug}`} className="btn btn-secondary btn-sm">
                    <span>Studi Kasus Proyek</span>
                    <ChevronRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center-cta">
            <Link to="/pelanggan-proyek" className="btn btn-primary">
              <span>{t('cta_view_projects')}</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* 5. Lokasi Operasional Preview (Section 5.1 & 5.6) */}
      <section className="section section-subtle locations-reference-section">
        <div className="container">
          <div className="grid-2 align-center">
            <div>
              <span className="accent-bar"></span>
              <span className="eyebrow">Jaringan Nasional</span>
              <h2 className="section-title">{t('section_locations_title')}</h2>
              <p className="about-desc-text">
                {t('section_locations_subtitle')}
              </p>
              
              <ul className="location-preview-list">
                {locations.slice(0, 4).map((loc) => (
                  <li key={loc.id}>
                    <MapPin size={18} className="icon-red" />
                    <div>
                      <strong>{loc.name}</strong> ({loc.type})
                      <div className="loc-city">{loc.city}, {loc.province}</div>
                    </div>
                  </li>
                ))}
              </ul>

              <Link to="/lokasi-operasional-kami" className="btn btn-primary">
                <span>Buka Peta Lokasi Interaktif</span>
                <ArrowRight size={16} />
              </Link>
            </div>

            <div className="map-placeholder-box">
              <div className="map-preview-overlay">
                <MapPin size={48} color="var(--color-brand-primary)" />
                <h4>Interactive Operational Map</h4>
                <p>30+ Batching Plants & Quarries</p>
                <Link to="/lokasi-operasional-kami" className="btn btn-outline-red btn-sm">
                  Lihat Peta Lengkap
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Keberlanjutan Banner (Section 5.1 & 5.9) */}
      <section className="section section-dark sustainability-reference-section">
        <div className="container">
          <div className="grid-2 align-center">
            <div>
              <span className="eyebrow" style={{ color: 'var(--color-brand-primary-light)' }}>Eco-Friendly Concrete</span>
              <h2 className="section-title">{t('section_sustainability_title')}</h2>
              <p className="section-subtitle" style={{ textAlign: 'left', margin: '0 0 24px 0' }}>
                {t('section_sustainability_subtitle')}
              </p>
              
              <div className="sustainability-check-list">
                <div className="check-item">
                  <CheckCircle size={18} color="var(--color-brand-primary)" />
                  <span>Teknologi Semen Blended Rendah Karbon</span>
                </div>
                <div className="check-item">
                  <CheckCircle size={18} color="var(--color-brand-primary)" />
                  <span>Pengolahan Air Daur Ulang Zero-Waste</span>
                </div>
                <div className="check-item">
                  <CheckCircle size={18} color="var(--color-brand-primary)" />
                  <span>Penghargaan PROPER Biru KemenLHK</span>
                </div>
              </div>

              <Link to="/keberlanjutan" className="btn btn-primary">
                <span>Pelajari Inisiatif Keberlanjutan</span>
                <ArrowRight size={16} />
              </Link>
            </div>

            <div className="video-thumbnail-box">
              <img src="/reference-images/sustainability.webp" alt="Sustainability Solusi Bangun Beton" />
              <Link to="/keberlanjutan" className="play-btn-circle">
                <Play size={28} fill="#ffffff" color="#ffffff" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Berita dan Artikel (Section 5.1 & 5.10) */}
      <section className="section news-reference-section">
        <div className="container">
          <div className="section-header">
            <span className="accent-bar"></span>
            <span className="eyebrow">Informasi Terbaru</span>
            <h2 className="section-title">{t('section_news_title')}</h2>
          </div>

          <div className="grid-3">
            {newsArticles.map((art) => (
              <div key={art.slug} className="sbb-card">
                <img src={art.image} alt={art.title} className="sbb-card-img" />
                <div className="sbb-card-body">
                  <span className="sbb-card-tag">{art.type} • {art.date}</span>
                  <h3 className="sbb-card-title">{art.title}</h3>
                  <p className="sbb-card-desc">{art.excerpt}</p>
                  <Link to={`/blog/${art.slug}`} className="btn btn-secondary btn-sm">
                    <span>{t('read_more')}</span>
                    <ChevronRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. CTA Band Kontak (Section 5.1) */}
      <section className="cta-band-section">
        <div className="container text-center">
          <h2 className="cta-band-title">Siap Memulai Proyek Konstruksi Anda?</h2>
          <p className="cta-band-subtitle">Dapatkan konsultasi teknis gratis dan penawaran beton siap pakai terbaik dari ahli Solusi Bangun Beton.</p>
          <div className="cta-band-buttons">
            <Link to="/kontak-kami" className="btn btn-primary">
              <span>Hubungi Tim Sales & Teknikal</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <style>{`
        .hero-slider {
          position: relative;
          height: min(760px, calc(100vh - var(--header-height)));
          min-height: 620px;
          width: 100%;
          overflow: hidden;
          background-color: var(--color-surface-dark);
        }

        .hero-slide {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          opacity: 0;
          transition: opacity 0.8s ease-in-out;
          display: flex;
          align-items: center;
        }

        .hero-slide.active {
          opacity: 1;
        }

        .hero-caption-box {
          width: min(48%, 650px);
          color: #ffffff;
          margin-left: auto;
          padding-right: 24px;
        }

        .hero-eyebrow {
          font-size: 16px;
          font-weight: 400;
          letter-spacing: 0.5px;
          color: rgba(255,255,255,0.8);
          text-transform: uppercase;
          display: block;
          margin-bottom: 12px;
        }

        .hero-title {
          font-size: 42px;
          font-weight: 700;
          line-height: 1.2;
          margin-bottom: 24px;
        }

        .hero-desc {
          font-size: 16px;
          color: rgba(255,255,255,0.9);
          margin-bottom: 32px;
          line-height: 1.6;
        }

        .hero-cta-group {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
        }

        .hero-dots {
          position: absolute;
          bottom: 24px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 10px;
          z-index: 10;
        }

        .hero-dots .dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background-color: rgba(255, 255, 255, 0.4);
          border: none;
          transition: all 0.3s ease;
        }

        .hero-dots .dot.active {
          background-color: #ffffff;
          width: 32px;
          border-radius: 6px;
        }

        .hero-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 12;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: rgba(255,255,255,0.92);
          color: var(--color-ink);
          display: grid;
          place-items: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.18);
          transition: transform 0.25s ease, background 0.25s ease;
        }

        .hero-arrow:hover {
          transform: translateY(-50%) scale(1.08);
          background: #ffffff;
        }

        .hero-arrow-prev { left: 20px; }
        .hero-arrow-next { right: 20px; }

        .about-reference-section {
          position: relative;
          overflow: hidden;
        }

        .about-reference-section::before {
          content: '';
          position: absolute;
          width: 420px;
          height: 420px;
          right: -100px;
          bottom: -130px;
          border-radius: 50%;
          background: var(--color-brand-accent-green);
          filter: blur(90px);
          opacity: 0.26;
          pointer-events: none;
        }

        .about-reference-section .container {
          position: relative;
          z-index: 1;
        }

        .about-reference-section .grid-2 {
          gap: 80px;
        }

        .about-desc-text {
          font-size: 16px;
          color: #555;
          line-height: 1.7;
          margin-bottom: 40px;
        }

        .img-rounded-shadow {
          width: 515px;
          height: 580px;
          margin-left: auto;
          object-fit: cover;
          border-radius: 8px;
          box-shadow: none;
        }

        .align-center {
          align-items: center;
        }

        .text-center-cta {
          text-align: center;
          margin-top: 48px;
        }

        .solutions-reference-section {
          position: relative;
          background: #ffffff;
          overflow: hidden;
        }

        .solutions-reference-section::before {
          content: '';
          position: absolute;
          inset: 40px 60px;
          background-color: #f2f2ef;
          background-image: linear-gradient(135deg, rgba(119,118,84,0.04) 25%, transparent 25%),
            linear-gradient(315deg, rgba(119,118,84,0.04) 25%, transparent 25%);
          background-size: 34px 34px;
        }

        .solutions-reference-section .container {
          position: relative;
          z-index: 1;
        }

        .services-carousel,
        .projects-carousel {
          display: flex;
          gap: 28px;
          overflow-x: auto;
          padding: 24px 2px 34px;
          scroll-snap-type: x mandatory;
          scrollbar-width: thin;
          scrollbar-color: var(--color-brand-primary) transparent;
        }

        .services-carousel .sbb-card {
          flex: 0 0 330px;
          scroll-snap-align: start;
          background: #ffffff;
        }

        .services-carousel .sbb-card-img {
          height: 300px;
        }

        .projects-reference-section {
          position: relative;
          padding-bottom: 120px;
          overflow: hidden;
          background: linear-gradient(to bottom, #fff 0%, #fff 72%, #f1f1ed 100%);
        }

        .projects-reference-section::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          height: 50px;
          background: var(--color-brand-accent-green);
        }

        .projects-carousel {
          gap: 32px;
          padding-bottom: 60px;
        }

        .project-feature-card {
          flex: 0 0 570px;
          min-height: 590px;
          scroll-snap-align: start;
          border: 0;
          position: relative;
          background: transparent;
        }

        .project-feature-card .sbb-card-img {
          height: 430px;
        }

        .project-feature-card .sbb-card-body {
          position: relative;
          width: calc(100% - 54px);
          margin: -56px 0 0 auto;
          background: #ffffff;
          z-index: 2;
          padding: 28px 30px;
        }

        .project-number {
          position: absolute;
          right: 24px;
          top: -72px;
          font-size: 76px;
          line-height: 1;
          font-weight: 300;
          color: #ffffff;
          text-shadow: 0 2px 14px rgba(0,0,0,0.32);
        }

        .location-preview-list {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 28px;
        }

        .location-preview-list li {
          display: flex;
          gap: 12px;
          align-items: flex-start;
          font-size: 14px;
        }

        .loc-city {
          font-size: 12px;
          color: var(--color-text-muted);
        }

        .map-placeholder-box {
          height: 380px;
          background:
            linear-gradient(rgba(0,0,0,0.34), rgba(0,0,0,0.34)),
            radial-gradient(circle at 25% 30%, #b9c1aa 0 3%, transparent 3.5%),
            linear-gradient(135deg, #d9ddd2 25%, #cbd2c5 25% 50%, #e1e4da 50% 75%, #c5cdc0 75%);
          background-size: cover;
          border-radius: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .map-preview-overlay {
          background: #ffffff;
          padding: 24px 32px;
          border-radius: 0;
          text-align: center;
          box-shadow: var(--shadow-hover);
        }

        .sustainability-check-list {
          display: flex;
          flex-direction: column;
          gap: 14px;
          margin-bottom: 32px;
        }

        .check-item {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 15px;
          font-weight: 500;
        }

        .video-thumbnail-box {
          position: relative;
          border-radius: 0;
          overflow: hidden;
        }

        .play-btn-circle {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 64px;
          height: 64px;
          background-color: var(--color-brand-primary);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 20px rgba(119, 118, 84, 0.5);
          transition: transform 0.2s ease;
        }

        .play-btn-circle:hover {
          transform: translate(-50%, -50%) scale(1.1);
        }

        .cta-band-section {
          background-color: var(--color-brand-accent-green);
          padding: 70px 0;
          border-top: 1px solid var(--color-border);
        }

        .cta-band-title {
          font-size: 32px;
          font-weight: 800;
          color: var(--color-ink);
          margin-bottom: 12px;
        }

        .cta-band-subtitle {
          font-size: 16px;
          color: var(--color-text-muted);
          max-width: 640px;
          margin: 0 auto 28px auto;
        }

        @media (max-width: 768px) {
          .hero-slider {
            height: 560px;
            min-height: 560px;
          }
          .hero-title {
            font-size: 24px;
          }
          .hero-desc {
            font-size: 15px;
          }
          .hero-caption-box {
            width: 100%;
            margin: 0;
            padding: 0 42px 0 12px;
          }
          .hero-arrow {
            width: 40px;
            height: 40px;
          }
          .hero-arrow-prev { left: 8px; }
          .hero-arrow-next { right: 8px; }
          .hero-slide {
            background-position: 43% center;
          }
          .hero-slide::before {
            content: '';
            position: absolute;
            inset: 0;
            background: rgba(0, 0, 0, .3);
          }
          .hero-container { position: relative; z-index: 1; }
          .about-reference-section .grid-2 {
            gap: 36px;
          }
          .img-rounded-shadow {
            width: 100%;
            height: 420px;
          }
          .solutions-reference-section::before {
            inset: 20px 0;
          }
          .services-carousel .sbb-card {
            flex-basis: 82vw;
          }
          .project-feature-card {
            flex-basis: 86vw;
            min-height: auto;
          }
          .project-feature-card .sbb-card-img {
            height: 320px;
          }
          .project-feature-card .sbb-card-body {
            width: calc(100% - 24px);
            margin-top: -32px;
          }
          .project-number {
            font-size: 54px;
            top: -52px;
          }
        }
      `}</style>
    </div>
  );
}
